/**
 * Les icônes d'une fiche, dans la console — les MÊMES tracés que l'application
 * (`apps/mobile/src/features/manuel-vivant/fiche/icones.tsx`) : ceux de Lucide
 * (licence ISC), que la maquette employait. Recopiés, parce que la console
 * n'a pas `react-native-svg` et que huit tracés ne valent pas une dépendance.
 *
 * Décoratives : chaque icône accompagne un MOT (« À revoir », « Juste ») et
 * sort de l'arbre d'accessibilité. Leur couleur est `currentColor` : c'est le
 * conteneur qui porte le rôle.
 */
import type { ReactNode } from 'react';

export type NomIcone =
  | 'ampoule'
  | 'oeil'
  | 'plume'
  | 'alerte'
  | 'signet'
  | 'coche'
  | 'question'
  | 'horloge'
  | 'retour'
  | 'liste';

const TRACES: Readonly<
  Record<NomIcone, { readonly d: readonly string[]; readonly cercles?: readonly number[][] }>
> = {
  ampoule: {
    d: [
      'M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5',
      'M9 18h6',
      'M10 22h4',
    ],
  },
  oeil: {
    d: [
      'M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0',
    ],
    cercles: [[12, 12, 3]],
  },
  plume: {
    d: [
      'M12 20h9',
      'M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z',
    ],
  },
  alerte: {
    d: [
      'm21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3',
      'M12 9v4',
      'M12 17h.01',
    ],
  },
  signet: { d: ['m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z'] },
  coche: { d: ['M20 6 9 17l-5-5'] },
  question: {
    d: ['M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3', 'M12 17h.01'],
    cercles: [[12, 12, 10]],
  },
  horloge: { d: ['M12 6v6l4 2'], cercles: [[12, 12, 10]] },
  retour: { d: ['M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8', 'M3 3v5h5'] },
  liste: { d: ['m3 17 2 2 4-4', 'm3 7 2 2 4-4', 'M13 6h8', 'M13 12h8', 'M13 18h8'] },
};

export function Icone({
  nom,
  taille = 16,
  epaisseur = 1.75,
}: {
  readonly nom: NomIcone;
  readonly taille?: number;
  readonly epaisseur?: number;
}): ReactNode {
  const trace = TRACES[nom];
  return (
    <svg
      className="fiche-icone"
      width={taille}
      height={taille}
      viewBox="0 0 24 24"
      strokeWidth={epaisseur}
      aria-hidden="true"
      focusable="false"
    >
      {trace.cercles?.map((c, i) => (
        <circle key={`c${i}`} cx={c[0]} cy={c[1]} r={c[2]} />
      ))}
      {trace.d.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}
